<template>
  <div>
    <div
      class="btn w-full lg:hidden"
      @click="filtersAreVisible = !filtersAreVisible"
    >
      Filter
    </div>

    <div
      class="bg-light hidden fixed z-[999] p-4 inset-0 lg:static lg:block lg:p-0 overflow-auto"
      :class="{ '!block': filtersAreVisible }"
    >
      <div
        class="bg-dark text-white absolute top-0 right-0 w-12 aspect-square grid place-items-center"
        @click="filtersAreVisible = !filtersAreVisible"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512"
          class="fill-current w-3 cursor-pointer"
        >
          <path
            d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
          />
        </svg>
      </div>
      <!-- Search -->
      <FormKit
        id="titleFilter"
        v-model="titleFilter"
        type="text"
        autocomplete="off"
        name="titleFilter"
        label-class="after:!content-none"
        :delay="400"
        :label="$t(`general.vacancySearchLabel`)"
        :value="filters.search"
      />

      <!-- Professions -->
      <HeadlessDisclosure
        v-slot="{ open }"
        as="div"
        class="border-b border-dark/20 pb-2 mt-5"
        :default-open="true"
      >
        <HeadlessDisclosureButton
          as="strong"
          class="select-none cursor-pointer flex gap-4 justify-between mb-2"
        >
          {{ $t("general.professions") }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            class="w-3 fill-current flex-none transition-transform"
            :class="{ 'rotate-180': open }"
          >
            <path
              d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
            />
          </svg>
        </HeadlessDisclosureButton>
        <TransitionExpand no-opacity>
          <HeadlessDisclosurePanel as="div">
            <div
              :class="{
                'h-[136px] overflow-hidden': !allProfessionsAreVisible,
              }"
            >
              <FormKit
                v-model="professionFilterIds"
                type="checkbox"
                decorator-icon="check"
                outer-class="!mb-0"
                :options="
                  professions?.map((e) => ({
                    value: e.id,
                    label: e.attributes.title,
                  }))
                "
              />
            </div>
            <div
              class="underline hover:no-underline mt-2 text-primary cursor-pointer"
              @click="allProfessionsAreVisible = !allProfessionsAreVisible"
            >
              Toon <template v-if="!allProfessionsAreVisible">alles</template
              ><template v-else>minder</template>
            </div>
          </HeadlessDisclosurePanel>
        </TransitionExpand>
      </HeadlessDisclosure>

      <!-- Hours -->
      <HeadlessDisclosure
        v-slot="{ open }"
        as="div"
        class="border-b border-dark/20 pb-2 mt-5"
        :default-open="true"
      >
        <HeadlessDisclosureButton
          as="strong"
          class="select-none cursor-pointer flex gap-4 justify-between mb-2"
        >
          {{ $t("form.hours.label") }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            class="w-3 fill-current flex-none transition-transform"
            :class="{ 'rotate-180': open }"
          >
            <path
              d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
            />
          </svg>
        </HeadlessDisclosureButton>
        <TransitionExpand no-opacity>
          <HeadlessDisclosurePanel as="div">
            <div
              class="mb-4"
              :class="{
                'h-[136px] overflow-hidden': !allHoursAreVisible,
              }"
            >
              <FormKit
                v-model="hoursFilterIds"
                type="checkbox"
                decorator-icon="check"
                outer-class="!mb-0"
                :options="
                  hours?.map((e) => ({
                    value: e.id,
                    label: e.label,
                  }))
                "
              />
            </div>
            <!-- This button is not necessary because the itemcount is < 6 -->
            <!-- <div
              @click="allHoursAreVisible = !allHoursAreVisible"
              class="underline hover:no-underline mt-2 text-primary cursor-pointer"
            >
              Toon <template v-if="!allHoursAreVisible">alles</template
              ><template v-else>minder</template>
            </div> -->
          </HeadlessDisclosurePanel>
        </TransitionExpand>
      </HeadlessDisclosure>

      <!-- Regions -->
      <HeadlessDisclosure
        v-slot="{ open }"
        as="div"
        class="border-b border-dark/20 pb-2 mt-5"
        :default-open="true"
      >
        <HeadlessDisclosureButton
          as="strong"
          class="select-none cursor-pointer flex gap-4 justify-between mb-2"
        >
          {{ $t("general.regions") }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            class="w-3 fill-current flex-none transition-transform"
            :class="{ 'rotate-180': open }"
          >
            <path
              d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
            />
          </svg>
        </HeadlessDisclosureButton>
        <TransitionExpand no-opacity>
          <HeadlessDisclosurePanel as="div">
            <div
              class="mb-4"
              :class="{
                'h-[136px] overflow-hidden': !allRegionsAreVisible,
              }"
            >
              <FormKit
                v-model="regionFilterIds"
                type="checkbox"
                decorator-icon="check"
                outer-class="!mb-0"
                :options="
                  regions?.map((e) => ({
                    value: e.id,
                    label: e.attributes.municipality,
                  }))
                "
              />
            </div>
            <!-- This button is not necessary because the itemcount is < 6 -->
            <!-- <div
              @click="allRegionsAreVisible = !allRegionsAreVisible"
              class="underline hover:no-underline mt-2 text-primary cursor-pointer"
            >
              Toon <template v-if="!allRegionsAreVisible">alles</template
              ><template v-else>minder</template>
            </div> -->
          </HeadlessDisclosurePanel>
        </TransitionExpand>
      </HeadlessDisclosure>

      <!-- Places, not yet necessary -->
      <!-- <HeadlessDisclosure
      v-slot="{ open }"
      as="div"
      class="border-b border-dark/20 pb-2 mt-5"
      :default-open="
        placeFilterIds &&
        Array.isArray(placeFilterIds) &&
        placeFilterIds[0] != null
          ? true
          : false
      "
    >
      <HeadlessDisclosureButton
        as="strong"
        class="select-none cursor-pointer flex gap-4 justify-between mb-2"
      >
        {{ $t("general.places") }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          class="w-3 fill-current flex-none transition-transform"
          :class="{ 'rotate-180': open }"
        >
          <path
            d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
          />
        </svg>
      </HeadlessDisclosureButton>
      <TransitionExpand no-opacity>
        <HeadlessDisclosurePanel as="div">
          <FormKit
            v-model="placeFilterIds"
            type="checkbox"
            decorator-icon="check"
            :options="
              places.map((e) => ({ value: e.id, label: e.attributes.title }))
            "
          />
        </HeadlessDisclosurePanel>
      </TransitionExpand>
    </HeadlessDisclosure> -->

      <!-- Employments -->
      <HeadlessDisclosure
        v-slot="{ open }"
        as="div"
        class="border-b border-dark/20 pb-2 mt-5"
        :default-open="true"
      >
        <HeadlessDisclosureButton
          as="strong"
          class="select-none cursor-pointer flex gap-4 justify-between mb-2"
        >
          {{ $t("general.employments") }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            class="w-3 fill-current flex-none transition-transform"
            :class="{ 'rotate-180': open }"
          >
            <path
              d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
            />
          </svg>
        </HeadlessDisclosureButton>
        <TransitionExpand no-opacity>
          <HeadlessDisclosurePanel as="div">
            <div
              :class="{
                'h-[136px] overflow-hidden': !allEmploymentsAreVisible,
              }"
            >
              <FormKit
                v-model="employmentFilterIds"
                type="checkbox"
                decorator-icon="check"
                outer-class="!mb-0"
                :options="
                  employments?.map((e) => ({
                    value: e.id,
                    label: e.attributes.title,
                  }))
                "
              />
            </div>
            <div
              class="underline hover:no-underline mt-2 text-primary cursor-pointer"
              @click="allEmploymentsAreVisible = !allEmploymentsAreVisible"
            >
              Toon <template v-if="!allEmploymentsAreVisible">alles</template
              ><template v-else>minder</template>
            </div>
          </HeadlessDisclosurePanel>
        </TransitionExpand>
      </HeadlessDisclosure>

      <!-- Educations -->
      <HeadlessDisclosure
        v-slot="{ open }"
        as="div"
        class="border-b border-dark/20 pb-2 mt-5"
        :default-open="true"
      >
        <HeadlessDisclosureButton
          as="strong"
          class="select-none cursor-pointer flex gap-4 justify-between mb-2"
        >
          {{ $t("general.educations") }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            class="w-3 fill-current flex-none transition-transform"
            :class="{ 'rotate-180': open }"
          >
            <path
              d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
            />
          </svg>
        </HeadlessDisclosureButton>
        <TransitionExpand no-opacity>
          <HeadlessDisclosurePanel as="div">
            <div
              :class="{
                'h-[136px] overflow-hidden': !allEducationsAreVisible,
              }"
            >
              <FormKit
                v-model="educationFilterIds"
                type="checkbox"
                decorator-icon="check"
                outer-class="!mb-0"
                :options="
                  educations?.map((e) => ({
                    value: e.id,
                    label: e.attributes.title,
                  }))
                "
              />
            </div>
            <div
              class="underline hover:no-underline mt-2 text-primary cursor-pointer"
              @click="allEducationsAreVisible = !allEducationsAreVisible"
            >
              Toon <template v-if="!allEducationsAreVisible">alles</template
              ><template v-else>minder</template>
            </div>
          </HeadlessDisclosurePanel>
        </TransitionExpand>
      </HeadlessDisclosure>

      <div
        class="btn w-full lg:hidden mt-6 sticky bottom-0"
        @click="filtersAreVisible = !filtersAreVisible"
      >
        Toon resultaten
      </div>

      <div class="text-center lg:text-right mt-4 lg:mt-5">
        <button
          class="btn-link font-bold"
          @click="((titleFilter = ''), clearFilters())"
        >
          {{ $t("general.resetFilters") }}
        </button>
      </div>
      <!-- Baantreffer banner - Op verzoek van Bas niet mee live gezet -->
      <!-- <a
        href="https://baantreffers.nl/"
        target="_blank"
        class="mt-6 hidden lg:block"
      >
        <img src="~/assets/images/baantreffers-banner.jpg" />
      </a> -->
    </div>
  </div>
</template>

<script setup lang="ts">
const { filters, applyFilter, clearFilters } = useVacancyFilters();
const { professions, regions, /*places,*/ employments, educations, hours } =
  useStaticData();

const props = defineProps<{
  filtersId: string;
}>();
const { filters: ssrFilters } = useFilters(props.filtersId);

const filtersAreVisible = ref(false);

const allProfessionsAreVisible = ref(false);
const allHoursAreVisible = ref(false);
const allRegionsAreVisible = ref(false);
const allEmploymentsAreVisible = ref(false);
const allEducationsAreVisible = ref(false);

if (filters.value.professions?.length > 0) {
  allProfessionsAreVisible.value = true;
}

if (filters.value.hours?.length > 0) {
  allHoursAreVisible.value = true;
}

if (filters.value.regions?.length > 0) {
  allRegionsAreVisible.value = true;
}

if (filters.value.employments?.length > 0) {
  allEmploymentsAreVisible.value = true;
}

if (filters.value.educations?.length > 0) {
  allEducationsAreVisible.value = true;
}

const titleFilter = ref(filters.value?.search ?? "");
watch(titleFilter, (newValue) => {
  applyFilter(newValue, "search");
});

const hoursFilterIds = computed({
  get(): number[] {
    return Array.isArray(filters.value.hours)
      ? filters.value.hours
      : [filters.value.hours as unknown as number];
  },
  set(newValue: number[]) {
    if (
      (newValue !== filters.value.hours && newValue.length > 0) ||
      (newValue.length === 0 &&
        filters.value.hours &&
        filters.value.hours.length > 0)
    ) {
      applyFilter(newValue, "hours");
    }
  },
});

const professionFilterIds = computed({
  get(): number[] {
    return Array.isArray(filters.value.professions)
      ? filters.value.professions
      : [filters.value.professions as unknown as number];
  },
  set(newValue: number[]) {
    if (
      (newValue &&
        Array.isArray(newValue) &&
        newValue !== filters.value.professions &&
        newValue.length > 0) ||
      (newValue.length === 0 &&
        filters.value.professions &&
        filters.value.professions.length > 0)
    ) {
      applyFilter(newValue, "professions");
    }
  },
});

const regionFilterIds = computed({
  get(): number[] {
    return Array.isArray(filters.value.regions)
      ? filters.value.regions
      : [filters.value.regions as unknown as number];
  },
  set(newValue: number[]) {
    if (
      (newValue !== filters.value.regions && newValue.length > 0) ||
      (newValue.length === 0 &&
        filters.value.regions &&
        filters.value.regions.length > 0)
    ) {
      applyFilter(newValue, "regions");
    }
  },
});

// const placeFilterIds = computed({
//   get(): number[] {
//     return filters.value.places as number[];
//   },
//   set(newValue: number[]) {
//     if (
//       (newValue !== filters.value.places && newValue.length > 0) ||
//       (newValue.length === 0 &&
//         filters.value.places &&
//         filters.value.places.length > 0)
//     ) {
//       applyFilter(newValue, "places");
//     }
//   },
// });

const employmentFilterIds = computed({
  get(): number[] {
    return Array.isArray(filters.value.employments)
      ? filters.value.employments
      : [filters.value.employments as unknown as number];
  },
  set(newValue: number[]) {
    if (
      (newValue !== filters.value.employments && newValue.length > 0) ||
      (newValue.length === 0 &&
        filters.value.employments &&
        filters.value.employments.length > 0)
    ) {
      applyFilter(newValue, "employments");
    }
  },
});

const educationFilterIds = computed({
  get(): number[] {
    return Array.isArray(filters.value.educations)
      ? filters.value.educations
      : [filters.value.educations as unknown as number];
  },
  set(newValue: number[]) {
    if (
      (newValue !== filters.value.educations && newValue.length > 0) ||
      (newValue.length === 0 &&
        filters.value.educations &&
        filters.value.educations.length > 0)
    ) {
      applyFilter(newValue, "educations");
    }
  },
});

const update = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const orSearchArray: any = [
    {
      title: {
        $containsi: filters.value.search,
      },
    },
    {
      company: {
        title: {
          $containsi: filters.value.search,
        },
      },
    },
  ];
  if (filters.value.search) {
    const searchArray = filters.value.search.split(" ");
    searchArray.forEach((word) => {
      orSearchArray.push({
        title: {
          $containsi: word,
        },
      });
      orSearchArray.push({
        jobDescription: {
          $containsi: word,
        },
      });
      orSearchArray.push({
        company: {
          title: {
            $containsi: word,
          },
        },
      });
      orSearchArray.push({
        places: {
          title: {
            $containsi: word,
          },
        },
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query: any = {
    $or: orSearchArray,
    status: {
      $eq: "open",
    },
    professions: {
      id: {
        $in: filters.value.professions ?? [],
      },
    },
    places: {
      id: {
        $in: filters.value.places ?? [],
      },
      region: {
        id: {
          $in: filters.value.regions ?? [],
        },
      },
    },
    employments: {
      id: {
        $in: filters.value.employments ?? [],
      },
    },
    educations: {
      id: {
        $in: filters.value.educations ?? [],
      },
    },
  };

  if (filters.value.hours && Array.isArray(filters.value.hours)) {
    const hourConditions = filters.value.hours
      .map((e) => {
        const hour = hours.value.find((h) => h.id == e);
        if (hour && hour.from != null && hour.to != null) {
          return {
            hoursFrom: { $lte: hour.to },
            hoursTo: { $gte: hour.from },
          };
        }
        return null;
      })
      .filter((condition) => condition !== null);

    if (hourConditions.length > 0) {
      query = {
        ...query,
        $or: hourConditions,
      };
    }
  }
  ssrFilters.value = query;
};

watch(filters, () => {
  update();
});
update();
</script>
